import { Events, emit } from './events.js';
// DEBUG: navbar module loaded
try { if (window.store && window.store.debug) window.store.log('[navbar] module loaded'); } catch (_) {}

class Navbar extends HTMLElement {
  connectedCallback() {
    try { if (window.store && window.store.debug) window.store.log('[navbar] connected', { threshold: this.getAttribute('scroll-threshold') }); } catch (_) {}

    const init = () => {
      try {
        const header = this.#getHeader();
        if (!header) { try { if (window.store && window.store.debug) window.store.log('[navbar] init aborted: header not found'); } catch (_) {} return; }
        try { if (window.store && window.store.debug) window.store.log('[navbar] init with header', { header }); } catch (_) {}

        // State sources
        this.__hovering = false;
        this.__menuOpen = false;
        this.__opaque = false;
        this.__activeOnHover = this.getAttribute('active-on-hover') !== 'false';
        this.__megaOpeningId = null;
        this.__megaOpeningRect = null;
        this.__megaOpeningDeadline = 0;
        this.__rafScheduled = false;
        this.__lastPointerEvent = null;
        this.__intendedMegaId = null;
        this.__openDialogs = new Set(); // Track all open dialog IDs

        this.#resetMegaTriggers();

        this.#applyInitialState();
        this.__onScroll = () => this.#updateOpaqueState();
        window.addEventListener('scroll', this.__onScroll, { passive: true });

        // Helpers
        // Align JS "desktop" with Tailwind md (50.9375rem / 815px) and dialog.js
        this.__isDesktop = () => window.matchMedia('(min-width: 50.9375rem)').matches;
        this.__openMega = (id) => {
          try {
            if (!id) return;
            this.__intendedMegaId = id;
            if (this.__openedMegaId && this.__openedMegaId !== id) { try { store.dialog.close(this.__openedMegaId); } catch (_) {} }
            // Track open time for click debounce (prevents touch pointerover + click race)
            this.__clickOpenedId = id;
            this.__clickOpenedAt = Date.now();
            this.#debugLog('mega:open', { id });
            this.#setMegaTriggerState(id, true);
            try {
              const headerRect = this.#getHeader()?.getBoundingClientRect() || null;
              const openingRect = this.#computeOpeningRect(headerRect, null);
              // Longer grace window to allow pointer to traverse toward panel
              this.#setMegaOpeningState(id, openingRect, 400);
            } catch (_) {}
            try { store.dialog.open(id); } catch (_) {}
            this.__openedMegaId = id;
            this.__hoverMega = true;
            this.#applyState();
          } catch(_) {}
        };
        // Header hover state + trigger open on desktop
        this.__onHeaderEnter = () => { this.#debugLog('header:pointerenter'); this.__hovering = true; this.#applyState(); };
        this.__onHeaderLeave = (e) => {
          if (e.target !== header) return;
          this.#debugLog('header:pointerleave');
          this.__hovering = false;
          // Do not close here; pointermove geometry decides
          this.#applyState();
          try { if (this.__hoverOutCloseTimer) { clearTimeout(this.__hoverOutCloseTimer); } } catch (_) {}
          // Fallback: small grace window to close if pointer is outside safe zones
          this.__hoverOutCloseTimer = setTimeout(() => {
            try {
              const hasOpenMega = !!(this.__openedMegaId || (document.querySelector('store-dialog[id^="mega-"][data-open]') != null));
              if (!hasOpenMega) return;
              // If another dialog (e.g., cart/menu) is open on mobile, keep mega open
              try {
                const otherOpen = document.querySelector('store-dialog[data-open]:not([id^="mega-"])');
              const isMobile = !this.__isDesktop();
                if (otherOpen && isMobile) return;
              } catch (_) {}
              // If not hovering header or mega panel anymore, close
              if (!this.__hovering && !this.__hoverMega) {
                const openEl = document.querySelector('store-dialog[id^="mega-"][data-open]');
                const openId = (openEl && openEl.id) || this.__openedMegaId || null;
                this.#closeMega('header:hover-out', openId);
              }
            } catch (_) {}
          }, 80);
        };
        header.addEventListener('pointerenter', this.__onHeaderEnter, false);
        header.addEventListener('pointerleave', this.__onHeaderLeave, false);
        this.__onHeaderOverTrigger = (ev) => {
          try {
            if (!this.__isDesktop()) return;
            const t = ev.target;
            if (!(t instanceof Node && header.contains(t))) return;
            const trigger = t.closest && t.closest('[data-mega-trigger],[aria-controls^="mega-"]');
            if (trigger) {
              const id = trigger.getAttribute('aria-controls');
              if (!id || !/^mega-/.test(id)) return;
              this.__intendedMegaId = id;
              if (this.__openedMegaId === id) { this.__hoverMega = true; this.#applyState(); return; }
              // Skip if we just click-closed this menu (prevent immediate reopen on hover)
              if (this.__clickClosedId === id && this.__clickClosedAt && (Date.now() - this.__clickClosedAt) < 300) {
                return;
              }
              this.#debugLog('header:trigger-hover', { id, trigger: this.#describeTarget(trigger) });
              this.__openMega(id);
              return;
            }
            // If hovering logo or any header action (non-mega trigger), close open mega
            const logo = t.closest && t.closest('[data-header-logo]');
            const actionable = t.closest && t.closest('a,button,[role="menuitem"]');
            const actionableIsTrigger = actionable && actionable.closest && actionable.closest('[data-mega-trigger],[aria-controls^="mega-"]');
            if ((logo || (actionable && !actionableIsTrigger)) && this.__openedMegaId) {
              this.#debugLog('header:non-trigger-hover-close', { target: this.#describeTarget(t) });
              this.#closeMega('header:non-trigger-hover-close');
            }
          } catch(_) {}
        };
        header.addEventListener('pointerover', this.__onHeaderOverTrigger, true);

        // Ensure logo hover explicitly closes mega on desktop
        try {
          const logoEls = Array.from(header.querySelectorAll('[data-header-logo]'));
          logoEls.forEach((logoEl) => {
            try {
              const onLogoEnter = () => {
                try {
                  if (!this.__isDesktop()) return;
                  if (this.__openedMegaId) this.#closeMega('logo:hover');
                } catch (_) {}
              };
              logoEl.addEventListener('pointerenter', onLogoEnter, true);
            } catch (_) {}
          });
        } catch (_) {}

        // Dialog-driven state (hamburger, mega, cart, etc.)
        this.__onMegaEnter = (ev) => {
          const target = ev && ev.target;
          const currentTarget = ev && ev.currentTarget;
          const isOverlay = this.#isOverlay(target);
          this.#debugLog('mega:pointerenter', {
            overlay: isOverlay,
            target: this.#describeTarget(target),
            currentTarget: this.#describeTarget(currentTarget)
          });
          if (isOverlay) { this.__hoverMega = false; return; }
          this.__hoverMega = true;
          this.#applyState();
        };
        this.__onMegaLeave = (ev) => {
          const target = ev && ev.target;
          const currentTarget = ev && ev.currentTarget;
          const isOverlay = this.#isOverlay(target);
          const leavingRoot = currentTarget && target === currentTarget;
          if (!leavingRoot && !isOverlay) {
            this.#debugLog('mega:pointerleave-ignored', {
              reason: 'descendant',
              target: this.#describeTarget(target),
              currentTarget: this.#describeTarget(currentTarget)
            });
            return;
          }
          this.#debugLog('mega:pointerleave', {
            overlay: isOverlay,
            leavingRoot,
            target: this.#describeTarget(target),
            currentTarget: this.#describeTarget(currentTarget)
          });
          if (isOverlay) { this.__hoverMega = false; return; }
          this.__hoverMega = false;
          // No immediate close; pointermove geometry handles this
          try { if (this.__hoverOutCloseTimer) { clearTimeout(this.__hoverOutCloseTimer); } } catch (_) {}
          this.__hoverOutCloseTimer = setTimeout(() => {
            try {
              const hasOpenMega = !!(this.__openedMegaId || (document.querySelector('store-dialog[id^="mega-"][data-open]') != null));
              if (!hasOpenMega) return;
              // If another dialog (e.g., cart/menu) is open on mobile, keep mega open
              try {
                const otherOpen = document.querySelector('store-dialog[data-open]:not([id^="mega-"])');
              const isMobile = !this.__isDesktop();
                if (otherOpen && isMobile) return;
              } catch (_) {}
              if (!this.__hovering && !this.__hoverMega) {
                const openEl = document.querySelector('store-dialog[id^="mega-"][data-open]');
                const openId = (openEl && openEl.id) || this.__openedMegaId || null;
                this.#closeMega('mega:hover-out', openId);
              }
            } catch (_) {}
          }, 80);
        };
        this.__onDialogOpen = (ev) => {
          this.__menuOpen = true;
          try {
            const id = ev && ev.detail && ev.detail.id;
            if (!id) return;

            // For mega menus, clean up any other open mega menus first (handles race conditions)
            if (/^mega-/.test(id)) {
              const staleMegas = [];
              this.__openDialogs.forEach(openId => {
                if (/^mega-/.test(openId) && openId !== id) {
                  staleMegas.push(openId);
                }
              });
              staleMegas.forEach(openId => {
                this.__openDialogs.delete(openId);
                this.#setMegaTriggerState(openId, false);
                try { store.dialog.close(openId); } catch (_) {}
                console.log('[navbar] force-closed stale mega:', openId);
              });
            }

            // Track all open dialogs
            this.__openDialogs.add(id);

            // Always log for debugging
            console.log('[navbar] dialog:open', id, 'triggers:', document.querySelectorAll(`[aria-controls="${id}"]`).length, 'total:', this.__openDialogs.size);

            // Update trigger button state (skip mega menus - they have their own system)
            if (!/^mega-/.test(id)) {
              document.querySelectorAll(`[aria-controls="${id}"]`).forEach(btn => {
                btn.setAttribute('data-dialog-active', '');
                btn.setAttribute('aria-expanded', 'true');
              });
            }

            // Update header dialog-open state
            const header = this.#getHeader();
            if (header) {
              header.setAttribute('data-dialog-open', '');
            }

            if (/^mega-/.test(id)) {
              // Ignore late openings for a previous trigger if user intent moved on
              if (this.__intendedMegaId && id !== this.__intendedMegaId) {
                try { store.dialog.close(id); } catch (_) {}
                return;
              }
              const dlg = document.getElementById(id);
              if (dlg) {
                try { dlg.addEventListener('pointerenter', this.__onMegaEnter, true); } catch (_) {}
                try { dlg.addEventListener('pointerleave', this.__onMegaLeave, true); } catch (_) {}
                this.__openedMegaId = id;
                try {
                  const headerRect = this.#getHeader()?.getBoundingClientRect() || null;
                  const panelEl = dlg.querySelector('.dialog-panel');
                  const panelRect = panelEl ? panelEl.getBoundingClientRect() : null;
                  const mergedRect = this.#computeOpeningRect(
                    headerRect,
                    panelRect && panelRect.width && panelRect.height ? panelRect : null
                  );
                  // Opening zone remains during initial movement
                  this.#setMegaOpeningState(id, mergedRect, 450);
                  if (panelEl && (!panelRect || !panelRect.width || !panelRect.height)) {
                    const raf = typeof requestAnimationFrame === 'function' ? requestAnimationFrame : (cb) => setTimeout(cb, 16);
                    raf(() => {
                      try {
                        const latestPanelRect = panelEl.getBoundingClientRect();
                        if (latestPanelRect && latestPanelRect.width && latestPanelRect.height) {
                          const headerRect2 = this.#getHeader()?.getBoundingClientRect() || headerRect;
                          const updatedRect = this.#computeOpeningRect(headerRect2, latestPanelRect);
                          this.#setMegaOpeningState(id, updatedRect, 400);
                        }
                      } catch (_) {}
                    });
                  }
                } catch (_) {}
              }
              this.#setMegaTriggerState(id, true);
              this.#debugLog('mega:dialog-open', { id });
            }
            if (window.store && window.store.debug) window.store.log('[navbar] dialog open', ev && ev.detail);
          } catch (_) {}
          this.#applyState();
        };
        this.__onDialogClose = (ev) => {
          this.__menuOpen = !!document.querySelector('store-dialog[data-open]');
          try {
            const id = ev && ev.detail && ev.detail.id;
            if (!id) return;

            // Remove from open dialogs set
            this.__openDialogs.delete(id);

            // Always log for debugging
            console.log('[navbar] dialog:close', id, 'remaining:', this.__openDialogs.size);

            // Update trigger button state (skip mega menus - they have their own system)
            if (!/^mega-/.test(id)) {
              document.querySelectorAll(`[aria-controls="${id}"]`).forEach(btn => {
                btn.removeAttribute('data-dialog-active');
                btn.setAttribute('aria-expanded', 'false');
              });
            }

            // Update header dialog-open state
            const header = this.#getHeader();
            if (header && this.__openDialogs.size === 0) {
              header.removeAttribute('data-dialog-open');
            }

            if (/^mega-/.test(id)) {
              const dlg = document.getElementById(id);
              if (dlg) {
                try { dlg.removeEventListener('pointerenter', this.__onMegaEnter, true); } catch (_) {}
                try { dlg.removeEventListener('pointerleave', this.__onMegaLeave, true); } catch (_) {}
              }
              this.#setMegaTriggerState(id, false);
              if (this.__openedMegaId === id) this.__openedMegaId = null;
              this.__hoverMega = false;
              this.#clearMegaOpeningState(id);
              this.#debugLog('mega:dialog-close', { id });
            }
            // When hamburger closes on mobile, also close any open mega dialogs
            try {
              if (id === 'menu') {
                const isMobile = !this.__isDesktop();
                if (isMobile) {
                  const openMegas = Array.from(document.querySelectorAll('store-dialog[id^="mega-"][data-open]'));
                  openMegas.forEach((dlg) => {
                    const mid = dlg.id;
                    try { this.#setMegaTriggerState(mid, false); } catch (_) {}
                    try { store.dialog.close(mid); } catch (_) {}
                    if (this.__openedMegaId === mid) this.__openedMegaId = null;
                  });
                  this.__hoverMega = false;
                }
              }
            } catch (_) {}
            if (window.store && window.store.debug) window.store.log('[navbar] dialog close', { anyOpen: this.__menuOpen });
          } catch (_) {}
          this.#applyState();
        };
        document.addEventListener('store:dialog:open', this.__onDialogOpen);
        document.addEventListener('store:dialog:close', this.__onDialogClose);

        // Close hamburger if viewport becomes desktop (>= md)
        this.__onResizeCloseMenu = () => {
          try {
            const isDesktop = this.__isDesktop();
            if (isDesktop) {
              const menu = document.getElementById('menu');
              if (menu && menu.hasAttribute('data-open')) {
                try { store.dialog.close('menu'); } catch (_) {}
              }
            }
          } catch (_) {}
        };
        window.addEventListener('resize', this.__onResizeCloseMenu);

        // Track pointer globally to determine if we're over header or current mega
        this.__onGlobalPointerMove = (ev) => {
          try {
            // rAF throttle to reduce work on fast mouse moves
            this.__lastPointerEvent = ev;
            if (this.__rafScheduled) return;
            this.__rafScheduled = true;
            const raf = typeof requestAnimationFrame === 'function' ? requestAnimationFrame : (cb) => setTimeout(cb, 16);
            raf(() => {
              this.__rafScheduled = false;
              const e = this.__lastPointerEvent;
              if (!e) return;
              const t = e.target;
              const headerEl = this.#getHeader();
              const megaEl = document.querySelector('store-dialog[id^="mega-"][data-open]') || null;
              // If any non-mega dialog (e.g., cart, menu) is open, don't auto-close mega via pointer geometry
              try {
                const otherOpen = document.querySelector('store-dialog[data-open]:not([id^="mega-"])');
                if (otherOpen && (megaEl || this.__openedMegaId)) {
                  this.#applyState();
                  return;
                }
              } catch (_) {}
              const el = t instanceof Element ? t : null;
              const triggerEl = el && el.closest && el.closest('[data-mega-trigger],[aria-controls^="mega-"]');
              const overlayPresent = megaEl ? !megaEl.hasAttribute('data-no-overlay') : undefined;
              const isOverlay = this.#isOverlay(t);
              const panelEl = megaEl && megaEl.querySelector && megaEl.querySelector('.dialog-panel');
              const inHeader = !!(headerEl && t instanceof Node && headerEl.contains(t));
              const panelRect = panelEl ? panelEl.getBoundingClientRect() : null;
              const inMegaArea = !!(panelEl && panelEl.contains && panelEl.contains(t));
              const inMegaByRect = panelRect ? this.#pointInRect(e, panelRect, 4) : false;
              const hoveredEl = (panelEl && typeof document !== 'undefined' && document.elementFromPoint && e && typeof e.clientX === 'number' && typeof e.clientY === 'number')
                ? document.elementFromPoint(e.clientX, e.clientY)
                : null;
              const inMegaByHit = !!(panelEl && hoveredEl && panelEl.contains(hoveredEl));
              const inMega = (inMegaArea || inMegaByRect || inMegaByHit) && !isOverlay;
              const inCorridor = panelEl ? this.#isInMegaCorridor(e, headerEl, panelEl) : false;
              const megaOpen = !!(megaEl && megaEl.hasAttribute && megaEl.hasAttribute('data-open'));
              let openingMega = !!this.__megaOpeningId;
              if (openingMega && this.__megaOpeningDeadline && this.#now() > this.__megaOpeningDeadline) {
                this.#clearMegaOpeningState(this.__megaOpeningId);
                openingMega = false;
              }
              // Update intended id based on current trigger under pointer
              try {
                if (triggerEl) {
                  const tid = triggerEl.getAttribute('aria-controls');
                  if (tid) this.__intendedMegaId = tid;
                }
              } catch (_) {}
              // Only honor opening zone for the intended/opened id to avoid wrong mega sticking
              const openingZoneOwner = this.__intendedMegaId || this.__openedMegaId || null;
              const inOpeningZone = openingMega && this.__megaOpeningId === openingZoneOwner && this.#isInOpeningZone(e, this.__megaOpeningRect);
              const keepMegaOpen = !!triggerEl || inMega || inHeader || inCorridor || inOpeningZone;

              // Fast switch correction: if hovering a different trigger, open its mega immediately
              try {
                if (this.__isDesktop && this.__isDesktop() && triggerEl) {
                  const id = triggerEl.getAttribute('aria-controls');
                  if (id && /^mega-/.test(id) && id !== this.__openedMegaId) {
                    // Skip if we just click-closed this menu
                    if (!(this.__clickClosedId === id && this.__clickClosedAt && (Date.now() - this.__clickClosedAt) < 300)) {
                      this.__openMega(id);
                    }
                  }
                }
              } catch (_) {}

              this.#debugLog('pointermove', {
                target: this.#describeTarget(t),
                hovered: this.#describeTarget(hoveredEl),
                coords: { x: e && typeof e.clientX === 'number' ? e.clientX : null, y: e && typeof e.clientY === 'number' ? e.clientY : null },
                panelRect: panelRect ? { top: panelRect.top, right: panelRect.right, bottom: panelRect.bottom, left: panelRect.left } : null,
                inHeader,
                inMega,
                inTrigger: !!triggerEl,
                inPanel: inMega,
                openedMegaId: this.__openedMegaId,
                hasOverlay: overlayPresent,
                isOverlay,
                inCorridor,
                openingMega,
                inOpeningZone,
                megaOpen
              });
              this.__hovering = inHeader || inCorridor || inOpeningZone;
              this.__hoverMega = inMega;
              if ((this.__openedMegaId || megaOpen || openingMega) && !keepMegaOpen) {
                const openId = this.__openedMegaId || (megaEl && megaEl.id) || null;
                this.#closeMega('pointermove', openId);
              }
              this.#applyState();
            });
          } catch (_) {}
        };
        document.addEventListener('pointermove', this.__onGlobalPointerMove, true);

        // Click behavior: desktop suppresses toggling for non-link triggers; mobile toggles
        this.__onHeaderClick = (ev) => {
          try {
            const trigger = ev.target && ev.target.closest && ev.target.closest('[aria-controls]');
            if (!trigger) return;
            const id = trigger.getAttribute('aria-controls');
            if (!id) return;

            // Handle non-mega dialogs (search, cart, localization) - always toggle
            if (!/^mega-/.test(id)) {
              const dlg = document.getElementById(id);
              if (dlg && dlg.hasAttribute('data-open')) {
                ev.preventDefault();
                ev.stopPropagation();
                try { store.dialog.close(id); } catch (_) {}
                console.log('[navbar] toggle-close non-mega:', id);
              }
              // Let inline onclick handle opening
              return;
            }
            const isLink = trigger.tagName === 'A' && !!trigger.getAttribute('href');
            if (this.__isDesktop()) {
              if (!isLink) {
                ev.preventDefault(); ev.stopPropagation();
                // Toggle on click - if already open, close it
                const el = document.getElementById(id);
                const isOpen = el && el.hasAttribute('data-open');
                console.log('[navbar] click handler:', id, 'isOpen:', isOpen, 'clickOpenedId:', this.__clickOpenedId, 'elapsed:', this.__clickOpenedAt ? (Date.now() - this.__clickOpenedAt) : 'N/A');
                if (isOpen) {
                  // Skip if we just opened this (prevents touch+click double event)
                  if (this.__clickOpenedId === id && this.__clickOpenedAt && (Date.now() - this.__clickOpenedAt) < 300) {
                    console.log('[navbar] skip close - just opened:', id);
                    return;
                  }
                  this.__clickClosedId = id;
                  this.__clickClosedAt = Date.now();
                  this.#closeMega('click-toggle', id);
                  console.log('[navbar] toggle-close mega:', id);
                } else {
                  this.__clickOpenedId = id;
                  this.__clickOpenedAt = Date.now();
                  this.__clickClosedId = null;
                  this.__openMega(id);
                }
                return;
              }
              return;
            }
            ev.preventDefault(); ev.stopPropagation();
            try {
              const el = document.getElementById(id);
              if (el && el.hasAttribute('data-open')) {
                // Skip if we just opened this (prevents touch+click double event)
                if (this.__clickOpenedId === id && this.__clickOpenedAt && (Date.now() - this.__clickOpenedAt) < 300) {
                  console.log('[navbar] skip close - just opened:', id);
                  return;
                }
                this.#debugLog('mega:toggle-close', { id, via: 'click' });
                this.#setMegaTriggerState(id, false);
                store.dialog.close(id);
              } else {
                this.__clickOpenedId = id;
                this.__clickOpenedAt = Date.now();
                this.#debugLog('mega:toggle-open', { id, via: 'click' });
                this.#setMegaTriggerState(id, true);
                store.dialog.open(id);
              }
            } catch(_) {}
          } catch(_) {}
        };
        header.addEventListener('click', this.__onHeaderClick, true);

        // Sync header height var (was in globals)
        try {
          const syncHeaderHeightVar = () => { try { const h = header.offsetHeight || 0; header.style.setProperty('--header-h', `${h}px`); document.documentElement && document.documentElement.style && document.documentElement.style.setProperty('--header-h', `${h}px`); } catch (_) {} };
          // Initial and common lifecycle syncs
          syncHeaderHeightVar();
          window.addEventListener('resize', syncHeaderHeightVar);
          try { window.addEventListener('load', syncHeaderHeightVar, { once: true }); } catch (_) {}
          try { if (document.fonts && document.fonts.ready && typeof document.fonts.ready.then === 'function') { document.fonts.ready.then(() => { try { syncHeaderHeightVar(); } catch (_) {} }).catch(() => {}); } } catch (_) {}
          // React to header content/layout changes (e.g., announcement bar show/hide)
          try {
            const ro = new ResizeObserver(() => { try { syncHeaderHeightVar(); } catch (_) {} });
            ro.observe(header);
            this.__cleanupRO = () => { try { ro.disconnect(); } catch (_) {} };
          } catch (_) { this.__cleanupRO = null; }
          // Also update when dialogs open/close as layout may nudge header
          const __syncOnDialog = () => { try { syncHeaderHeightVar(); } catch (_) {} };
          document.addEventListener('store:dialog:open', __syncOnDialog);
          document.addEventListener('store:dialog:close', __syncOnDialog);
          this.__cleanupSyncHeader = () => {
            try { window.removeEventListener('resize', syncHeaderHeightVar); } catch (_) {}
            try { document.removeEventListener('store:dialog:open', __syncOnDialog); } catch (_) {}
            try { document.removeEventListener('store:dialog:close', __syncOnDialog); } catch (_) {}
            try { if (this.__cleanupRO) this.__cleanupRO(); } catch (_) {}
          };
        } catch (_) {}

        // Mobile drawer behavior intentionally removed; handled by dedicated module later

        // Mega menu hover/close behavior
        // this.#initMega(); // removed: dialogs now drive header state

        // Initial compute
        this.#updateOpaqueState();
        this.#applyState();
      } catch (e) { try { if (window.store && window.store.debug) window.store.log('[navbar] init error', e); } catch (_) {} }
    };

    // If header not present yet, retry briefly
    const h = this.#getHeader();
    if (!h) {
      try { if (window.store && window.store.debug) window.store.log('[navbar] header not found, retrying...'); } catch (_) {}
      let tries = 0;
      const timer = setInterval(() => {
        tries += 1;
        const hh = this.#getHeader();
        if (hh) { clearInterval(timer); init(); }
        else if (tries >= 20) { clearInterval(timer); try { if (window.store && window.store.debug) window.store.log('[navbar] header not found after retries'); } catch (_) {} }
      }, 100);
      return;
    }
    init();
  }
  disconnectedCallback() {
    try {
      if (window.store && window.store.debug) window.store.log('[navbar] disconnected');
      window.removeEventListener('scroll', this.__onScroll);
      const header = this.#getHeader();
      if (header) {
        try { header.removeEventListener('pointerenter', this.__onPointerEnter, false); } catch(_) {}
        try { header.removeEventListener('pointerleave', this.__onPointerLeave, false); } catch(_) {}
        try { header.removeEventListener('pointerenter', this.__onHeaderEnter, false); } catch(_) {}
        try { header.removeEventListener('pointerleave', this.__onHeaderLeave, false); } catch(_) {}
        try { header.removeEventListener('pointerover', this.__onHeaderOverTrigger, true); } catch(_) {}
        try { header.removeEventListener('click', this.__onHeaderClick, true); } catch(_) {}
      }
      try { document.removeEventListener('pointermove', this.__onGlobalPointerMove, true); } catch (_) {}
      // removed global open-on-move listener
      document.removeEventListener('store:dialog:open', this.__onDialogOpen);
      document.removeEventListener('store:dialog:close', this.__onDialogClose);
      try { window.removeEventListener('resize', this.__onResizeCloseMenu); } catch (_) {}
      if (this.__cleanupSyncHeader) this.__cleanupSyncHeader();
      try { const headerEl = this.#getHeader(); if (headerEl && this.__onHeaderClick) headerEl.removeEventListener('click', this.__onHeaderClick); if (this.__onKeydown) document.removeEventListener('keydown', this.__onKeydown); } catch (_) {}
    } catch (_) {}
  }

  #getHeader() { try { return this.closest('[data-header="true"]'); } catch (_) { return null; } }
  #applyInitialState() { const h=this.#getHeader(); if (!h) return; h.removeAttribute('data-opaque'); h.removeAttribute('data-active'); }
  #getMegaTriggers() {
    try {
      const header = this.#getHeader();
      if (!header) return [];
      return Array.from(header.querySelectorAll('[data-mega-trigger],[aria-controls^="mega-"]'));
    } catch (_) {
      return [];
    }
  }
  #findMegaTriggerById(id) {
    if (!id) return null;
    try {
      return this.#getMegaTriggers().find((trigger) => {
        const targetId = trigger.getAttribute('aria-controls');
        return targetId && targetId === id;
      }) || null;
    } catch (_) {
      return null;
    }
  }
  #setMegaTriggerState(id, isActive) {
    try {
      if (!id) return;
      const trigger = this.#findMegaTriggerById(id);
      console.log('[navbar] setMegaTriggerState', id, isActive, 'trigger:', trigger ? 'found' : 'NOT FOUND');
      if (!trigger) return;
      if (isActive) {
        this.#getMegaTriggers().forEach((el) => {
          if (el === trigger) return;
          el.removeAttribute('data-mega-open');
          el.setAttribute('aria-expanded', 'false');
          el.classList?.remove('is-open');
        });
        trigger.setAttribute('data-mega-open', 'true');
        trigger.setAttribute('aria-expanded', 'true');
        trigger.classList?.add('is-open');
      } else {
        trigger.removeAttribute('data-mega-open');
        trigger.setAttribute('aria-expanded', 'false');
        trigger.classList?.remove('is-open');
      }
    } catch (_) {}
  }
  #resetMegaTriggers() {
    try {
      this.#getMegaTriggers().forEach((trigger) => {
        trigger.removeAttribute('data-mega-open');
        trigger.setAttribute('aria-expanded', 'false');
        trigger.classList?.remove('is-open');
      });
    } catch (_) {}
  }
  #closeMega(reason = 'unspecified', id = null) {
    try {
      const megaId = id || this.__openedMegaId;
      if (!megaId) return;
      this.#debugLog('mega:close', { reason, id: megaId });
      if (this.__openedMegaId === megaId) this.__openedMegaId = null;
      this.__hoverMega = false;
      this.#clearMegaOpeningState(megaId);
      try { this.#setMegaTriggerState(megaId, false); } catch (_) {}
      try { store.dialog.close(megaId); } catch (_) {}
    } catch (_) {}
  }
  #isOverlay(node) {
    try {
      if (!node || !(node instanceof Node)) return false;
      return !!(node.closest && node.closest('[data-modal-overlay]'));
    } catch (_) {
      return false;
    }
  }
  #pointInRect(ev, rect, padding = 0) {
    try {
      if (!ev || !rect) return false;
      const x = typeof ev.clientX === 'number' ? ev.clientX : null;
      const y = typeof ev.clientY === 'number' ? ev.clientY : null;
      if (x == null || y == null) return false;
      const left = rect.left - padding;
      const right = rect.right + padding;
      const top = rect.top - padding;
      const bottom = rect.bottom + padding;
      return x >= left && x <= right && y >= top && y <= bottom;
    } catch (_) {
      return false;
    }
  }
  #setMegaOpeningState(id, rect, duration = 500) {
    try {
      if (!id) return;
      this.__megaOpeningId = id;
      this.__megaOpeningRect = rect;
      this.__megaOpeningDeadline = rect ? this.#now() + duration : 0;
    } catch (_) {}
  }
  #clearMegaOpeningState(id = null) {
    try {
      if (id && this.__megaOpeningId && this.__megaOpeningId !== id) return;
      this.__megaOpeningId = null;
      this.__megaOpeningRect = null;
      this.__megaOpeningDeadline = 0;
    } catch (_) {}
  }
  // No debounce-based close; closing is decided purely by geometry in pointermove
  #computeOpeningRect(headerRect, panelRect = null) {
    try {
      const viewportWidth = Math.max(
        typeof window !== 'undefined' && window.innerWidth ? window.innerWidth : 0,
        typeof document !== 'undefined' && document.documentElement ? document.documentElement.clientWidth : 0
      );
      const padding = 32; // a bit more forgiving
      const safeHeader = headerRect || { left: 0, right: viewportWidth, bottom: 0, top: 0, height: 80 };
      let left = Math.min(safeHeader.left, 0) - padding;
      let right = Math.max(safeHeader.right, viewportWidth || safeHeader.right) + padding;
      let top = (safeHeader.bottom || 0) - 24;
      // Extend bottom generously to include likely panel area during anim
      let bottom = (safeHeader.bottom || 0) + Math.max(600, (safeHeader.height || (safeHeader.bottom - safeHeader.top) || 80) * 10);
      if (panelRect && panelRect.width && panelRect.height) {
        left = Math.min(left, panelRect.left - padding);
        right = Math.max(right, panelRect.right + padding);
        top = Math.min(top, panelRect.top - padding);
        bottom = Math.max(bottom, panelRect.bottom + padding);
      }
      if (!viewportWidth) {
        right = Math.max(right, left + 1024);
      }
      return { left, right, top, bottom };
    } catch (_) {
      return null;
    }
  }
  #isInMegaCorridor(ev, headerEl, panelEl) {
    try {
      if (!ev || !headerEl || !panelEl) return false;
      const x = typeof ev.clientX === 'number' ? ev.clientX : null;
      const y = typeof ev.clientY === 'number' ? ev.clientY : null;
      if (x == null || y == null) return false;
      const headerRect = headerEl.getBoundingClientRect();
      const panelRect = panelEl.getBoundingClientRect();
      if (!headerRect || !panelRect) return false;
      const top = headerRect.bottom;
      const bottom = panelRect.top;
      if (bottom <= top) return false;
      const margin = 16; // slightly narrower corridor for faster close on content hover
      const left = Math.min(headerRect.left, panelRect.left) - margin;
      const right = Math.max(headerRect.right, panelRect.right) + margin;
      return x >= left && x <= right && y >= top && y <= bottom;
    } catch (_) {
      return false;
    }
  }
  #isInOpeningZone(ev, rect) {
    try {
      if (!ev || !rect) return false;
      const x = typeof ev.clientX === 'number' ? ev.clientX : null;
      const y = typeof ev.clientY === 'number' ? ev.clientY : null;
      if (x == null || y == null) return false;
      return x >= rect.left && x <= rect.right && y >= rect.top && y <= rect.bottom;
    } catch (_) {
      return false;
    }
  }
  #describeTarget(node) {
    try {
      if (!node || !(node instanceof Element)) return String(node);
      const tag = node.tagName.toLowerCase();
      const id = node.id ? `#${node.id}` : '';
      const cls = node.className ? `.${String(node.className).trim().replace(/\s+/g, '.')}` : '';
      const attrs = node.getAttributeNames ? node.getAttributeNames().reduce((acc, name) => {
        if (/^data-/.test(name)) {
          acc[name] = node.getAttribute(name);
        }
        return acc;
      }, {}) : undefined;
      return { tag, id, cls, attrs };
    } catch (_) {
      return '[unknown]';
    }
  }
  #debugLog(event, payload) {
    try {
      if (!(window.store && window.store.debug)) return;
      window.store.log('[navbar][debug]', event, payload);
    } catch (_) {}
  }
  #now() {
    try {
      if (typeof performance !== 'undefined' && performance.now) return performance.now();
    } catch (_) {}
    return Date.now();
  }
  #resolveMode() {
    try {
      const header = this.#getHeader();
      const headerMode = (header?.getAttribute('data-navbar-mode') || header?.dataset?.navbarMode || '').toLowerCase();
      const attrMode = (this.getAttribute('mode') || '').toLowerCase();
      const resolved = headerMode || attrMode || 'scroll';
      return resolved;
    } catch (_) {
      return 'scroll';
    }
  }
  #resolveScrollOffsetRem() {
    try {
      const header = this.#getHeader();
      const headerOffset = header?.getAttribute('data-navbar-scroll-offset') || header?.dataset?.navbarScrollOffset;
      const attrOffset = this.getAttribute('scroll-offset-rem');
      const resolved = headerOffset ?? attrOffset;
      if (resolved == null || resolved === '') return null;
      return resolved;
    } catch (_) {
      return null;
    }
  }
  #updateOpaqueState() {
    const header = this.#getHeader(); if (!header) return;
    const mode = this.#resolveMode();
    if (mode === 'always') {
      this.__opaque = true;
      return this.#applyState();
    }

    // Prefer rem-based offset from top; fallback to legacy viewport ratio
    const remAttr = this.#resolveScrollOffsetRem();
    const remVal = remAttr != null ? parseFloat(remAttr) : NaN;
    let thresholdPx = NaN;
    if (!Number.isNaN(remVal)) {
      try {
        const rootFs = parseFloat(getComputedStyle(document.documentElement).fontSize) || 16;
        thresholdPx = Math.max(0, Math.round(remVal * rootFs));
      } catch (_) { thresholdPx = Math.max(0, Math.round(remVal * 16)); }
    }
    if (Number.isNaN(thresholdPx)) {
      const ratio = parseFloat(this.getAttribute('scroll-threshold') || '0.25');
      thresholdPx = Math.max(1, Math.round((window.innerHeight || 0) * (Number.isNaN(ratio) ? 0.25 : ratio)));
    }

    const y = window.scrollY || 0;
    this.__opaque = y >= thresholdPx;
    try { if (window.store && window.store.debug) window.store.log('[navbar] scroll', { y, thresholdPx, opaque: this.__opaque }); } catch (_) {}
    this.#applyState();
  }
  #applyState() {
    const header = this.#getHeader(); if (!header) { try { if (window.store && window.store.debug) window.store.log('[navbar] applyState: header missing'); } catch (_) {} return; }
    const mode = this.#resolveMode();
    const isDesktop = (typeof this.__isDesktop === 'function' ? this.__isDesktop() : window.matchMedia('(min-width: 50.9375rem)').matches);
    header.toggleAttribute('data-opaque', !!this.__opaque);
    // Active only when scrolled/hovered/mega/dialog engaged, or when mode is 'always'
    const hoverEngaged = (typeof this.__isDesktop === 'function' ? this.__isDesktop() : isDesktop) && this.__activeOnHover && this.__hovering;
    const hasOpenDialogs = this.__openDialogs && this.__openDialogs.size > 0;
    const active = (mode === 'always') || this.__opaque || this.__hoverMega || this.__menuOpen || hoverEngaged || hasOpenDialogs;
    header.toggleAttribute('data-active', active);

    // Decide background state strictly in JS (CSS does not force it anymore)
    // Transparent at top for all viewports: only use active bg when scrolled or engaged
    const shouldUseActiveBg = (mode === 'always') || this.__opaque || this.__hoverMega || this.__menuOpen || hoverEngaged || hasOpenDialogs;
    try {
      if (shouldUseActiveBg) {
        header.style.setProperty('--bg-color', 'var(--header-bg-active)');
        header.style.setProperty('--bg-color-md', 'var(--header-bg-active)');
      } else {
        header.style.setProperty('--bg-color', 'var(--header-bg-initial)');
        header.style.setProperty('--bg-color-md', 'var(--header-bg-initial)');
      }
      this.#debugLog('state:apply', {
        isDesktop,
        hovering: this.__hovering,
        hoverMega: this.__hoverMega,
        dialogOpen: !!this.__menuOpen,
        opaque: this.__opaque,
        active,
        openedMegaId: this.__openedMegaId,
        shouldUseActiveBg,
        bg: getComputedStyle(header).getPropertyValue('--bg-color').trim(),
        headerAttrs: header.getAttributeNames ? header.getAttributeNames().reduce((a,k)=>{a[k]=header.getAttribute(k);return a;},{}) : undefined
      });
    } catch(_) {}

    emit(Events.header.state, { header, active, scrolled: !!this.__opaque, mega: false, menuDrawer: false, subdrawer: false }, header);
  }

  #initMega() {
    const header = this.#getHeader(); if (!header) { try { if (window.store && window.store.debug) window.store.log('[navbar] initMega: header missing'); } catch (_) {} return; }
    const root = (() => { try { return header.querySelector('[data-menu-root]') || header.querySelector('#mega-root'); } catch(_) { return null; } })();
    if (!root) { try { if (window.store && window.store.debug) window.store.log('[navbar] initMega: root not found'); } catch (_) {} return; }
    try { if (window.store && window.store.debug) window.store.log('[navbar] initMega root found', root); } catch (_) {}

    const drawerOpen = () => false;
    const TRIGGER_SEL = '[data-mega],[data-mega-target],[aria-controls^="mega-"],.btn,.header-btn,.shopify-block [role="menuitem"],a,button';
    const ACTIVE_SEL = 'a,button,[data-mega],[data-mega-target],[aria-controls^="mega-"]';
    const CLOSER_SEL = '[data-header-logo]';
    const slugify = (s) => {
      try {
        return String(s || '')
          .toLowerCase()
          .normalize('NFKD')
          .replace(/[\u0300-\u036f]/g, '')
          .replace(/&/g, 'and')
          .replace(/[^a-z0-9]+/g, '-')
          .replace(/^-+|-+$/g, '');
      } catch (_) { return ''; }
    };
    const panelKeySet = () => new Set(panels().map((p) => p.getAttribute('data-mega-panel')).filter(Boolean));
    const keyOf = (el) => {
      if (!el) return '';
      const k1 = el.getAttribute?.('data-mega'); if (k1) return k1;
      const k2 = el.getAttribute?.('data-mega-target'); if (k2) return k2;
      const ac = el.getAttribute?.('aria-controls');
      if (ac) { const m = ac.match(/^mega-(.+?)-panel$/); if (m && m[1]) return m[1]; return ac; }
      // Fallback: infer from text content against known panel keys
      try {
        const keys = panelKeySet();
        if (!keys.size) return '';
        const text = slugify(el.textContent || el.getAttribute?.('aria-label') || '');
        if (text && keys.has(text)) return text;
        // Try closest meaningful ancestor (e.g., button > div.contents)
        const parentText = slugify(el.closest?.('a,button,.btn,[role="menuitem"]')?.textContent || '');
        if (parentText && keys.has(parentText)) return parentText;
      } catch (_) {}
      return '';
    };
    const esc = (s) => (window.CSS && CSS.escape ? CSS.escape(s) : s);
    const panels = () => Array.from(root.querySelectorAll('[data-mega-panel]'));
    const triggers = () => Array.from(header.querySelectorAll(TRIGGER_SEL));
    const getOpenKey = () => String(root.getAttribute('data-open') || '');

    // Dev-only: validate triggers vs panels when debug enabled
    try {
      if (window.store && window.store.debug) {
        const triggerKeys = new Set(triggers().map((t) => keyOf(t)).filter(Boolean));
        const panelKeys = new Set(panels().map((p) => p.getAttribute('data-mega-panel')).filter(Boolean));
        const missingPanels = Array.from(triggerKeys).filter((k) => !panelKeys.has(k));
        const orphanPanels = Array.from(panelKeys).filter((k) => !triggerKeys.has(k));
        if (missingPanels.length) window.store.log('[navbar][dev] Missing panels for trigger keys:', missingPanels);
        if (orphanPanels.length) window.store.log('[navbar][dev] Orphan panels without triggers:', orphanPanels);
      }
    } catch (_) {}

    panels().forEach(p => {
      p.classList.remove('is-open');
      const usesHiddenClass = p.classList.contains('hidden');
      if (!usesHiddenClass) { p.hidden = true; p.dataset.usesHiddenClass = '0'; }
      else { p.dataset.usesHiddenClass = '1'; }
    });

    let inactiveTimer = null; let closeTimer = null; let overHeader = false; let overRoot = false;
    const cancelTimer = () => { if (inactiveTimer) { clearTimeout(inactiveTimer); inactiveTimer = null; } };
    const ensureActive = () => { if (!drawerOpen()) { header.setAttribute('data-active','true'); } };
    const scheduleInactive = (ms=120) => { if (drawerOpen()) return; cancelTimer(); inactiveTimer = setTimeout(() => { inactiveTimer = null; if (header.hasAttribute('data-scrolled')) return; if (header.hasAttribute('data-menu')) return; header.removeAttribute('data-active'); }, ms); };

    const clearActive = () => {
      if (drawerOpen()) return;
      header.removeAttribute('data-menu');
      root.removeAttribute('data-open');
      panels().forEach(p => {
        p.classList.remove('is-open');
        if (p.dataset.usesHiddenClass === '1') { if (!p.classList.contains('hidden')) p.classList.add('hidden'); }
        else { p.hidden = true; }
      });
      triggers().forEach(t => t.classList.remove('is-open'));
      emit(Events.menu.close, { header }, header);
    };

    const closePanelsKeepActive = () => {
      if (drawerOpen()) return;
      header.removeAttribute('data-menu');
      root.removeAttribute('data-open');
      panels().forEach(p => {
        p.classList.remove('is-open');
        if (p.dataset.usesHiddenClass === '1') { if (!p.classList.contains('hidden')) p.classList.add('hidden'); }
        else { p.hidden = true; }
      });
      triggers().forEach(t => t.classList.remove('is-open'));
      ensureActive();
      emit(Events.menu.close, { header }, header);
    };

    const setActive = (key) => {
      if (drawerOpen()) return;
      const panel = root.querySelector(`[data-mega-panel="${esc(key)}"]`);
      if (!panel) return clearActive();
      const current = getOpenKey();
      if (current === String(key) && header.hasAttribute('data-menu')) { ensureActive(); return; }
      header.setAttribute('data-menu','true');
      header.setAttribute('data-active','true');
      root.setAttribute('data-open', key);
      emit(Events.header.state, { header, active: true, scrolled: header.hasAttribute('data-scrolled'), mega: true, menuDrawer: false, subdrawer: header.hasAttribute('data-subdrawer') }, header);
      root.dataset.justOpenedAt = String(Date.now());
      panels().forEach(p => {
        p.classList.remove('is-open');
        if (p.dataset.usesHiddenClass === '1') { if (!p.classList.contains('hidden')) p.classList.add('hidden'); }
        else { p.hidden = true; }
      });
      if (panel.dataset.usesHiddenClass === '1') { panel.classList.remove('hidden'); }
      else { panel.hidden = false; }
      panel.classList.add('is-open');
      triggers().forEach(t => t.classList.toggle('is-open', keyOf(t) === key));
      emit(Events.menu.open, { key, panel, header }, header);
    };

    header.addEventListener('pointerover', (e) => {
      if (drawerOpen()) return;
      const trigger = e.target.closest?.(TRIGGER_SEL);
      if (trigger && header.contains(trigger)) {
        const key = keyOf(trigger);
        try { if (window.store && window.store.debug) window.store.log('[navbar] pointerover trigger', trigger, 'key=', key); } catch (_) {}
        if (key) { setActive(key); return; }
      }
      const actionable = e.target.closest?.(ACTIVE_SEL);
      const closer = e.target.closest?.(CLOSER_SEL);
      const insidePanel = actionable && root.contains(actionable);
      if (actionable && header.contains(actionable)) {
        ensureActive();
        if (closer && header.contains(closer) && !insidePanel && header.hasAttribute('data-menu')) { try { if (window.store && window.store.debug) window.store.log('[navbar] pointerover closer -> closePanels'); } catch (_) {} closePanelsKeepActive(); }
        const isMenuTrigger = actionable.matches && actionable.matches(TRIGGER_SEL);
        if (!isMenuTrigger && !insidePanel && header.hasAttribute('data-menu')) { try { if (window.store && window.store.debug) window.store.log('[navbar] pointerover non-trigger outside panel -> closePanels'); } catch (_) {} closePanelsKeepActive(); }
      }
    }, true);

    header.addEventListener('focusin', (e) => { if (drawerOpen()) return; const trigger = e.target.closest?.(TRIGGER_SEL); if (trigger && header.contains(trigger)) { setActive(keyOf(trigger)); } });

    const updateHoverState = () => {
      if (drawerOpen()) return;
      if (overHeader || overRoot) { if (closeTimer) { clearTimeout(closeTimer); closeTimer = null; } ensureActive(); return; }
      if (closeTimer) clearTimeout(closeTimer);
      const justOpenedAt = Number(root.dataset.justOpenedAt || 0);
      const delay = justOpenedAt && Date.now() - justOpenedAt < 150 ? 180 : 120;
      closeTimer = setTimeout(() => { closeTimer = null; clearActive(); if (!header.hasAttribute('data-scrolled')) scheduleInactive(120); else cancelTimer(); }, delay);
    };

    header.addEventListener('pointerenter', () => { overHeader = true; updateHoverState(); }, false);
    header.addEventListener('pointerleave', (e) => { if (e.target !== header) return; overHeader = false; setTimeout(() => { updateHoverState(); }, 0); }, false);
    root.addEventListener('pointerenter', () => { overRoot = true; updateHoverState(); }, false);
    root.addEventListener('pointerleave', (e) => { if (e.target !== root) return; overRoot = false; setTimeout(() => { updateHoverState(); }, 0); }, false);

    document.addEventListener('pointermove', (e) => {
      if (drawerOpen()) return; const t = e.target; if (!(t instanceof Node)) return; const nowOverHeader = header.contains(t); const nowOverRoot = root.contains(t); if (nowOverHeader !== overHeader || nowOverRoot !== overRoot) { overHeader = nowOverHeader; overRoot = nowOverRoot; updateHoverState(); }
    }, true);

    header.addEventListener('keydown', (e) => { if (e.key === 'Escape') { try { if (window.store && window.store.debug) window.store.log('[navbar] esc -> clearActive'); } catch (_) {} clearActive(); } });
    window.addEventListener('blur', () => { clearActive(); });
  }
}

try {
  const tags = ['store-navbar'];
  tags.forEach((TAG) => {
    if (!customElements.get(TAG)) {
      customElements.define(TAG, Navbar);
      try { if (window.store && window.store.debug) window.store.log(`[navbar] custom element defined as <${TAG}>`); } catch (_) {}
    }
  });
} catch (e) { try { if (window.store && window.store.debug) window.store.log('[navbar] define error', e); } catch (_) {} }


